import React, { useState, useEffect, useCallback } from 'react';
import { FaClock, FaCode, FaCalendarAlt } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import ThemedTimePicker from '../../../common/ThemedTimePicker';

const CRON_PRESETS = [
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Every day at 8am', value: '0 8 * * *' },
  { label: 'Every Monday at 9am', value: '0 9 * * 1' },
  { label: 'Every 1st of month at 7am', value: '0 7 1 * *' },
  { label: 'Weekdays at 6pm', value: '0 18 * * 1-5' },
];

const DAY_LABELS = [
  { key: 0, short: 'Sun', full: 'Sunday' },
  { key: 1, short: 'Mon', full: 'Monday' },
  { key: 2, short: 'Tue', full: 'Tuesday' },
  { key: 3, short: 'Wed', full: 'Wednesday' },
  { key: 4, short: 'Thu', full: 'Thursday' },
  { key: 5, short: 'Fri', full: 'Friday' },
  { key: 6, short: 'Sat', full: 'Saturday' },
];

const parseCron = (cron) => {
  if (!cron || typeof cron !== 'string') return null;
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  if (month !== '*') return null;

  // Hourly: N * * * *
  if (hour === '*' && dayOfMonth === '*' && dayOfWeek === '*') {
    const min = parseInt(minute, 10);
    if (!isNaN(min) && min >= 0 && min <= 59) {
      return {
        frequency: 'hourly',
        minute: min,
        time: '08:00',
        days: [1, 2, 3, 4, 5],
        dayOfMonth: 1,
      };
    }
    return null;
  }

  const hr = parseInt(hour, 10);
  const min = parseInt(minute, 10);
  if (isNaN(hr) || isNaN(min) || hr < 0 || hr > 23 || min < 0 || min > 59) return null;
  const time = `${String(hr).padStart(2, '0')}:${String(min).padStart(2, '0')}`;

  // Monthly: N N N * *
  if (dayOfMonth !== '*' && dayOfWeek === '*') {
    const dom = parseInt(dayOfMonth, 10);
    if (!isNaN(dom) && dom >= 1 && dom <= 31) {
      return { frequency: 'monthly', minute: min, time, days: [1, 2, 3, 4, 5], dayOfMonth: dom };
    }
    return null;
  }

  // Daily: N N * * *
  if (dayOfMonth === '*' && dayOfWeek === '*') {
    return { frequency: 'daily', minute: min, time, days: [1, 2, 3, 4, 5], dayOfMonth: 1 };
  }

  // Weekly: N N * * <days>
  if (dayOfMonth === '*' && dayOfWeek !== '*') {
    const parsedDays = parseDayOfWeek(dayOfWeek);
    if (parsedDays && parsedDays.length > 0) {
      return { frequency: 'weekly', minute: min, time, days: parsedDays, dayOfMonth: 1 };
    }
    return null;
  }

  return null;
};

const parseDayOfWeek = (dow) => {
  const days = new Set();

  const segments = dow.split(',');
  for (const seg of segments) {
    if (seg.includes('-')) {
      const [start, end] = seg.split('-').map(Number);
      if (isNaN(start) || isNaN(end)) return null;
      for (let i = start; i <= end; i++) days.add(i);
    } else {
      const n = parseInt(seg, 10);
      if (isNaN(n) || n < 0 || n > 7) return null;
      days.add(n === 7 ? 0 : n);
    }
  }

  return Array.from(days).sort((a, b) => a - b);
};

const describeCron = (cron) => {
  if (!cron) return '';
  const parsed = parseCron(cron);
  if (!parsed) {
    const parts = cron.trim().split(/\s+/);
    if (parts.length === 5) return `Custom: ${cron}`;
    return '';
  }

  const { frequency, minute, time, days, dayOfMonth } = parsed;
  const [hr, mn] = time.split(':').map(Number);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  const timeStr = `${hr12}:${String(mn).padStart(2, '0')} ${ampm}`;

  switch (frequency) {
    case 'hourly':
      return minute === 0 ? 'Every hour on the hour' : `Every hour at minute ${minute}`;
    case 'daily':
      return `Every day at ${timeStr}`;
    case 'weekly': {
      const weekdays = [1, 2, 3, 4, 5];
      const weekend = [0, 6];
      const allDays = [0, 1, 2, 3, 4, 5, 6];
      const sorted = [...days].sort((a, b) => a - b);

      if (JSON.stringify(sorted) === JSON.stringify(weekdays)) return `Weekdays at ${timeStr}`;
      if (JSON.stringify(sorted) === JSON.stringify(weekend)) return `Weekends at ${timeStr}`;
      if (JSON.stringify(sorted) === JSON.stringify(allDays)) return `Every day at ${timeStr}`;

      const dayNames = sorted.map((d) => DAY_LABELS.find((l) => l.key === d)?.short || d);
      return `Every ${dayNames.join(', ')} at ${timeStr}`;
    }
    case 'monthly': {
      const suffix =
        dayOfMonth === 1
          ? 'st'
          : dayOfMonth === 2
            ? 'nd'
            : dayOfMonth === 3
              ? 'rd'
              : dayOfMonth === 21
                ? 'st'
                : dayOfMonth === 22
                  ? 'nd'
                  : dayOfMonth === 23
                    ? 'rd'
                    : dayOfMonth === 31
                      ? 'st'
                      : 'th';
      return `Monthly on the ${dayOfMonth}${suffix} at ${timeStr}`;
    }
    default:
      return '';
  }
};

const RecurrencePicker = ({ value, onChange }) => {
  const [mode, setMode] = useState('visual');
  const [frequency, setFrequency] = useState('daily');
  const [time, setTime] = useState('08:00');
  const [minute, setMinute] = useState(0);
  const [days, setDays] = useState([1, 2, 3, 4, 5]);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [rawCron, setRawCron] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;
    if (!value) {
      setInitialized(true);
      return;
    }

    const parsed = parseCron(value);
    if (parsed) {
      setMode('visual');
      setFrequency(parsed.frequency);
      setTime(parsed.time);
      setMinute(parsed.minute);
      setDays(parsed.days);
      setDayOfMonth(parsed.dayOfMonth);
    } else {
      setMode('advanced');
      setRawCron(value);
    }
    setInitialized(true);
  }, [value, initialized]);

  const buildCron = useCallback(() => {
    const [hr, mn] = time.split(':').map(Number);
    switch (frequency) {
      case 'hourly':
        return `${minute} * * * *`;
      case 'daily':
        return `${mn} ${hr} * * *`;
      case 'weekly': {
        const sorted = [...days].sort((a, b) => a - b);
        if (sorted.length === 0) return `${mn} ${hr} * * *`;
        return `${mn} ${hr} * * ${sorted.join(',')}`;
      }
      case 'monthly':
        return `${mn} ${hr} ${dayOfMonth} * *`;
      default:
        return '';
    }
  }, [frequency, time, minute, days, dayOfMonth]);

  useEffect(() => {
    if (!initialized) return;
    if (mode === 'visual') {
      const cron = buildCron();
      if (cron && cron !== value) {
        onChange({ target: { name: 'cronExpression', value: cron } });
      }
    }
  }, [frequency, time, minute, days, dayOfMonth, mode, initialized, buildCron]);

  const handleRawChange = (newVal) => {
    setRawCron(newVal);
    onChange({ target: { name: 'cronExpression', value: newVal } });
  };

  const switchToVisual = () => {
    const parsed = parseCron(rawCron || value);
    if (parsed) {
      setFrequency(parsed.frequency);
      setTime(parsed.time);
      setMinute(parsed.minute);
      setDays(parsed.days);
      setDayOfMonth(parsed.dayOfMonth);
    }
    setMode('visual');
  };

  const switchToAdvanced = () => {
    setRawCron(value || buildCron());
    setMode('advanced');
  };

  const toggleDay = (day) => {
    setDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const setWeekdays = () => setDays([1, 2, 3, 4, 5]);
  const setWeekend = () => setDays([0, 6]);
  const setEveryDay = () => setDays([0, 1, 2, 3, 4, 5, 6]);

  const description = describeCron(value);

  return (
    <div className="space-y-3">
      {/* Mode toggle */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Recurrence <span className="text-red-500">*</span>
        </label>
        <button
          type="button"
          onClick={mode === 'visual' ? switchToAdvanced : switchToVisual}
          className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-primary-600 transition-colors"
        >
          {mode === 'visual' ? <FaCode size={10} /> : <FaCalendarAlt size={10} />}
          {mode === 'visual' ? 'Advanced' : 'Visual'}
        </button>
      </div>

      {mode === 'visual' ? (
        <div className="space-y-3">
          {/* Frequency selector */}
          <ThemedSelect
            name="frequency"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            className="w-full text-sm"
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </ThemedSelect>

          {/* Hourly: minute picker */}
          {frequency === 'hourly' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">At minute</label>
              <ThemedSelect
                name="minute"
                value={minute}
                onChange={(e) => setMinute(parseInt(e.target.value, 10))}
                className="w-32 text-sm"
              >
                {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                  <option key={m} value={m}>
                    :{String(m).padStart(2, '0')}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          )}

          {/* Daily/Weekly/Monthly: time picker */}
          {frequency !== 'hourly' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Time</label>
              <ThemedTimePicker value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          )}

          {/* Weekly: day-of-week toggles */}
          {frequency === 'weekly' && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs text-gray-500">Days</label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={setWeekdays}
                    className={`px-1.5 py-0.5 text-[10px] rounded border transition-colors ${
                      JSON.stringify([...days].sort()) === JSON.stringify([1, 2, 3, 4, 5])
                        ? 'bg-primary-50 text-primary-700 border-primary-300'
                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    Weekdays
                  </button>
                  <button
                    type="button"
                    onClick={setWeekend}
                    className={`px-1.5 py-0.5 text-[10px] rounded border transition-colors ${
                      JSON.stringify([...days].sort()) === JSON.stringify([0, 6])
                        ? 'bg-primary-50 text-primary-700 border-primary-300'
                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    Weekend
                  </button>
                  <button
                    type="button"
                    onClick={setEveryDay}
                    className={`px-1.5 py-0.5 text-[10px] rounded border transition-colors ${
                      JSON.stringify([...days].sort()) === JSON.stringify([0, 1, 2, 3, 4, 5, 6])
                        ? 'bg-primary-50 text-primary-700 border-primary-300'
                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    Every day
                  </button>
                </div>
              </div>
              <div className="flex gap-1">
                {DAY_LABELS.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => toggleDay(d.key)}
                    className={`flex-1 py-1.5 text-xs font-medium rounded border transition-colors ${
                      days.includes(d.key)
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'bg-surface text-gray-500 border-gray-200 hover:border-gray-300'
                    }`}
                    title={d.full}
                  >
                    {d.short}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Monthly: day-of-month */}
          {frequency === 'monthly' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Day of month</label>
              <ThemedSelect
                name="dayOfMonth"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(parseInt(e.target.value, 10))}
                className="w-32 text-sm"
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <input
              type="text"
              value={rawCron}
              onChange={(e) => handleRawChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., 0 8 * * * (every day at 8am)"
            />
            <p className="mt-1 text-xs text-gray-400">
              Format: minute hour day-of-month month day-of-week
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 mb-1.5">Quick presets:</p>
            <div className="flex flex-wrap gap-1.5">
              {CRON_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => handleRawChange(preset.value)}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    (rawCron || value) === preset.value
                      ? 'bg-primary-50 text-primary-700 border-primary-300'
                      : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Human-readable preview */}
      {description && (
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <FaClock size={10} className="text-primary-500 flex-shrink-0" />
          <span className="text-xs text-gray-700">{description}</span>
          {value && <span className="ml-auto text-[10px] font-mono text-gray-400">{value}</span>}
        </div>
      )}
    </div>
  );
};

export default RecurrencePicker;
